import { call, fork, put, select, take, takeLatest } from 'redux-saga/effects';

// Store
import { RootState } from '../../store';

// Mapper
import { TConvertLibCompounds } from 'api/libraries/libraries.mappers';

// Actions
import * as actionsCreator from './reducer';
import { modalClose } from 'reducers/modal';
import { hideLoader } from 'reducers/loader';
import { showMessage } from 'reducers/messages';
import { getTimeSuccess } from 'pages/Timer/reducer';
import { getListProjects } from 'pages/Projects/reducer';

// Lib
import { convertToPriceSelect, priceAvailable } from 'lib/prices';
import { isEngLang, optionsPrices } from 'lib/helpers';

// Types
import { Compound } from 'lib/prices';
import { ApiResponse as ApiResponseLib } from 'pages/LibrariesProduct/type';

function* _select<T>(fn: (state: RootState) => T) {
	const res: T = yield select(fn);
	return res;
}

export const settings = (store: RootState) => store.settings;
export const librariesCompounds = (store: RootState) => store.librariesCompounds;

// Generic response
export type ApiResponse<Data extends unknown> = {
	data?: Data;
	ok?: boolean;
	status?: number;
};
type TActions = {
	type: string,
	payload: string
}
type TMap = {
	[index: string]: string | number | boolean
}

export function* librariesCompoundsSagaStart(api: any, action: any) {
	yield fork(searchLibCompoundsWatcher, api);
	yield fork(addAllLibrariesToProjectWatcher, api, action);
	yield fork(getLibrariesCompoundsByIdWatcher, api, action);
}


/**
 * Search text libraries compounds worker sagas
 *
 * @param {function} api - api object
 * @returns
 */
export function* searchLibCompoundsWatcher(api: any): Generator<any, void, any> {
	yield takeLatest(
		actionsCreator.searchLibrariesCompounds.type,
		searchLibCompoundsTextWorker,
		api
	);
}

/**
 * Search text libraries compounds workers sagas
 *
 * @param {function} api - api object
 * @param {object} action - action from dispatch
 * @returns
 */
export function* searchLibCompoundsTextWorker(api: any, action: any): Generator<any, void, any> {
	try {
		const value = action.payload.value;
		const step = action.payload.step || 0;
		const cutNumber = action.payload.cutNumber;

		const settingsState = yield* _select(settings);
		const state = yield* _select(librariesCompounds);

		const inputValue = state.value;
		const isFilterByStock = state.isFilterByStock;
		const accessibility = state?.accessibility?.value;

		const isEng = isEngLang(value);

		if (!value) {
			return;
		}

		if (isEng) {
			const formData = {
				value: value,
				size: state.data.size,
				isFilter: isFilterByStock,
				page: inputValue ? step : 0,
				availability: accessibility
			};

			const res = yield call(
				api.request,
				'searchLibCompounds',
				formData,
				cutNumber
			);

			if (!res.ok) {
				yield put(actionsCreator.searchLibrariesCompoundsError(res.data));
				yield put(showMessage({
					type: 'info',
					message: 'Error in request search'
				}));
			} else {
				const data = res?.data?.content || [];

				const formData = {
					checkboxAll: false,
					dataSearch: {
						...res.data,
						content: data?.length ? data?.map((item: Compound) => {
							return {
								...item,
								quantity: 1,
								selected: false,
								currentTypePrice: 'EBC',
								currentPrice: settingsState.currentPrice
							};
						}) : []
					}
				};

				yield put(actionsCreator.searchLibrariesCompoundsSuccess(formData || {}));
			}

			yield put(hideLoader());
		} else {
			yield put(showMessage({
				type: 'info',
				text: 'Please use Latin letters only'
			}));
		}
	} catch (e) {
		console.log('ERROR searchLibCompoundsTextWorker', e);
	}
}

/**
 *  Get size compounds lib by id watcher Sagas starter
 *
 * @param {function} api - api object
 * @param {object} action - action from dispatch
 * @returns
 */
export function* getLibrariesCompoundsByIdWatcher(api: any, action: any): Generator<any, void, any> {
	while (true) {
		try {
			const actions: TActions = yield take(actionsCreator.getLibrariesCompounds.type);


			const res: ApiResponseLib<any> = yield call(api.request, 'getLibFullDetails', actions.payload);
			const compoundsCount = res.data?.lib?.compoundsCount || 0;

			yield put(actionsCreator.updateTitle({
				name: res.data?.lib?.name || '',
				compoundsCount: compoundsCount,
			}));

			if(!res.ok) {
				yield put(actionsCreator.getLibrariesCompoundsError());
				return;
			}

			yield put(getTimeSuccess({ data: 'start', key: 'libCompounds' }));
			let allData: Compound[] = [];
			const CHUNK_SIZE = 2000;

			const settingsState = yield* _select(settings);
			const state = yield* _select(librariesCompounds);
			const accessibility = state?.accessibility?.value;

			const chunksCount = Math.ceil((compoundsCount || 0) / CHUNK_SIZE);
			for (let i = 0; i < chunksCount; i++) {
				const chunkFormData = {
					size: CHUNK_SIZE,
					page: i,
					id: actions.payload,
					accessibility: accessibility
				};

				const res: ApiResponse<TConvertLibCompounds> = yield call(api.request, 'getLibCompounds', chunkFormData);

				if (!res.ok) {
					yield put(actionsCreator.getLibrariesCompoundsError());
					yield put(getTimeSuccess({ data: 'stop', key: 'libCompounds' }));
					return;
				}

				const chunkContent = res?.data?.content || [];
				// @ts-ignore
				allData = allData.concat(chunkContent);

				const partialData = allData.map((item: Compound) => {
					const optionsPr = item?.prices[0].prices?.length
						? item?.prices[0].prices?.map((item: TMap) =>
							optionsPrices(item)
						) : [];

					let countPrice = 0;
					let updateOptions: TMap[] = optionsPr;
					updateOptions = optionsPr.map((item: TMap) => {
						if (item.value === undefined) {
							countPrice += 1;
							return item;
						}
						countPrice = 0;
						return item;
					});

					if (updateOptions.length === countPrice) {
						updateOptions = updateOptions.map((item: TMap) => convertToPriceSelect(item));
					}

					const mainPrice = {
						type: 'Usd',
						mg: settingsState.currentPrice.mg,
						value: settingsState.currentPrice.value,
						price: settingsState.currentPrice.price
					};

					const isPOA = updateOptions.length === countPrice;

					return {
						...item,
						quantity: 1,
						isPOA,
						currentTypePrice: 'EBC',
						currentPrice: Object.values(mainPrice).length ? mainPrice : [],
						selected: !!(!item.isPOA && item.onStock && priceAvailable(item)?.available),
					};
				});
				yield put(actionsCreator.getLibrariesCompoundsPartial({
					data: {
						content: partialData,
						size: compoundsCount
					},
					dataOld: {
						size: compoundsCount,
						content: partialData
					},
					chunk:  i + 1,
					totalChunks: chunksCount,
				}));
			}
			yield put(actionsCreator.getLibrariesCompoundsSuccess());
			yield put(getTimeSuccess({ data: 'stop', key: 'libCompounds' }));
		} catch (e) {
			console.log('CATCH ERROR in getLibrariesCompoundsByIdWatcher: ', e);
		}
	}
}

/**
 *  Add all libraries to project watcher Sagas starter
 *
 * @param {function} api - api object
 * @param {object} action - action from dispatch
 * @returns
 */
export function* addAllLibrariesToProjectWatcher(api: any, action: any): Generator<any, void, any> {
	while (true) {
		try {
			const actions: any = yield take(actionsCreator.addAllLibrariesToProject.type);

			const url = actions?.payload?.url || '';
			const navigate = actions?.payload?.navigate;

			const productId = actions.payload.productId;
			const catNumber = actions.payload.catNumber;
			const prepackage = actions.payload.prepackage;

			const formData = {
				catNumber: catNumber,
				prepackage: prepackage
			};

			const res: ApiResponse<any> = yield call(
				api.request,
				'addAllToProject',
				formData,
				productId
			);

			if (!res.ok) {
				yield put(actionsCreator.addAllLibrariesToProjectError());
			} else {
				yield put(showMessage({
					type: 'info',
					title: 'Compound Added to project',
					text: 'Review or continue browsing. Contact<br/> us for any questions. Thank you!'
				}));
				yield put(getListProjects());
				yield put(actionsCreator.addAllLibrariesToProjectSuccess());
				yield put(modalClose());

				if (!!navigate) {
					navigate(url);
				}
			}
		} catch (e) {
			console.log('CATCH ERROR in addAllLibrariesToProjectWatcher: ', e);
		}
	}
}
